import api from './api';

/**
 * Build the multipart body for an upload.
 *
 * The card and the file travel together on create: a ruleset without a version cannot
 * validate anything, and sending them in one request is what lets the backend freeze
 * datasetType and nemsisVersion from the start.
 *
 * @param {Object} fields - Plain values to send
 * @param {File} [file] - The .sch itself
 *
 * @return {FormData}
 */
function toFormData(fields, file = null) {
  const form = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    // FormData stringifies everything, and "false" is truthy on the other side.
    form.append(key, typeof value === 'boolean' ? Number(value) : value);
  });

  if (file) {
    form.append('file', file);
  }

  return form;
}

// The NEMSIS versions do not change while the page is open, and three separate screens
// need them, so the answer is kept rather than re-fetched.
let metaCache = null;

export default {
  /**
   * NEMSIS versions: the one the platform submits today, and the ones a ruleset may be
   * uploaded for.
   *
   * @return Promise<Object>
   */
  getMeta() {
    if (metaCache) {
      return Promise.resolve(metaCache);
    }

    return api()
      .get('/schematrons/meta')
      .then((response) => {
        metaCache = response.data;
        return metaCache;
      });
  },

  /**
   * Get the ruleset catalogue.
   *
   * @param {Object} [filters] - scope, datasetType, nemsisVersion, state, clientId,
   *                             isActive, search
   *
   * @return Promise<Object>
   */
  getSchematrons(filters = {}) {
    return api()
      .get('/schematrons', { params: filters })
      .then(response => response.data);
  },

  /**
   * Get one ruleset with its whole version history.
   *
   * @param {number} schematronId - Ruleset identifier
   *
   * @return Promise<Object>
   */
  getSchematron(schematronId) {
    return api()
      .get(`/schematrons/${schematronId}`)
      .then(response => response.data);
  },

  /**
   * Create a ruleset from its first file. Compilation happens inside this request, so it
   * takes a few seconds - callers should show progress rather than assume it is instant.
   *
   * @param {Object} fields - Card fields plus releasedAt and notes
   * @param {File} file - The .sch to compile
   *
   * @return Promise<Object>
   */
  createSchematron(fields, file) {
    return api()
      .post('/schematrons', toFormData(fields, file))
      .then(response => response.data);
  },

  /**
   * Update the card. Dataset and NEMSIS version are refused once a version exists.
   *
   * @param {number} schematronId - Ruleset identifier
   * @param {Object} fields - Card fields
   *
   * @return Promise<Object>
   */
  updateSchematron(schematronId, fields) {
    return api()
      .put(`/schematrons/${schematronId}`, fields)
      .then(response => response.data);
  },

  /**
   * Delete a ruleset and every file its versions point at.
   *
   * @param {number} schematronId - Ruleset identifier
   *
   * @return Promise<Object>
   */
  deleteSchematron(schematronId) {
    return api()
      .delete(`/schematrons/${schematronId}`)
      .then(response => response.data);
  },

  /**
   * Upload a new version. It is compiled but does not become the published one.
   *
   * @param {number} schematronId - Ruleset identifier
   * @param {Object} fields - releasedAt and notes
   * @param {File} file - The .sch to compile
   *
   * @return Promise<Object>
   */
  createVersion(schematronId, fields, file) {
    return api()
      .post(`/schematrons/${schematronId}/versions`, toFormData(fields, file))
      .then(response => response.data);
  },

  /**
   * Make a version the one validation runs.
   *
   * @param {number} versionId - Version identifier
   *
   * @return Promise<Object>
   */
  publishVersion(versionId) {
    return api()
      .post(`/schematrons/versions/${versionId}/publish`)
      .then(response => response.data);
  },

  /**
   * Delete a version. The published one cannot go.
   *
   * @param {number} versionId - Version identifier
   *
   * @return Promise<Object>
   */
  deleteVersion(versionId) {
    return api()
      .delete(`/schematrons/versions/${versionId}`)
      .then(response => response.data);
  },

  /**
   * Download the original .sch of a version.
   *
   * @param {number} versionId - Version identifier
   *
   * @return Promise<Blob>
   */
  downloadVersionSource(versionId) {
    return api()
      .get(`/schematrons/versions/${versionId}/source`, { responseType: 'blob' })
      .then(response => response.data);
  },

  /**
   * Everything a client is concerned with: what runs, what is excluded, what could be
   * attached. Computed, not stored - national and state rulesets reach a client without
   * any row of their own.
   *
   * @param {number} clientId - Client identifier
   * @param {string} [nemsisVersion] - Restrict to one NEMSIS version
   *
   * @return Promise<Object>
   */
  getClientSchematrons(clientId, nemsisVersion = null) {
    return api()
      .get(`/clients/${clientId}/schematrons`, { params: nemsisVersion ? { nemsisVersion } : {} })
      .then(response => response.data);
  },

  /**
   * Record a decision for a client: attach a ruleset it would not get by itself, or
   * exclude it from one it would.
   *
   * @param {number} clientId - Client identifier
   * @param {number} schematronId - Ruleset identifier
   * @param {boolean} isEnabled - Whether it should apply
   *
   * @return Promise<Object>
   */
  setClientSchematron(clientId, schematronId, isEnabled) {
    return api()
      .put(`/clients/${clientId}/schematrons/${schematronId}`, { isEnabled })
      .then(response => response.data);
  },

  /**
   * Drop the decision so the client follows the default again. Not the same as
   * excluding it.
   *
   * @param {number} clientId - Client identifier
   * @param {number} schematronId - Ruleset identifier
   *
   * @return Promise<Object>
   */
  resetClientSchematron(clientId, schematronId) {
    return api()
      .delete(`/clients/${clientId}/schematrons/${schematronId}`)
      .then(response => response.data);
  },
};
